import {
  ACCESS_URL,
  AGENTS_URL,
  CONNECTORS_URL,
  CUSTOMER_BRANDS_URL,
  INTEGRITY_REPORT_URL,
  CONNECTOR_LIBRARY_URL,
  DIGEST_URL,
  EXPORT_GROUPS_URL,
  EXPORT_URL,
  HUB_ACCOUNTS_URL,
  HUB_SETTINGS_URL,
  PROFILE_URL,
  RUN_SCHEDULED_AGENTS_URL,
  SOURCE_INTEGRATIONS_URL,
  SYSTEM_PROMPT_URL,
} from 'features/settings/paths'
import { ClientAuthPolicy, ClientAuthRole } from 'src/types/auth-role'
import type { ClientSettings } from 'types/project'

export type SettingsPageKey = keyof ClientSettings['project']['pages']

/**
 * How the source-integrations page reads on screen, in one place.
 *
 * The sidebar item, the breadcrumb, and the page's own heading all name the same
 * page, so they read this rather than each carrying a copy. Exported from the
 * settings feature's root because the project layout's breadcrumb needs it too.
 */
export const SOURCE_INTEGRATIONS_LABEL = 'Source Integrations'

export const SETTINGS_PAGE_KEYS = {
  ACCESS: 'access',
  DATASETS: 'datasets',
  DATASET_ACCOUNTS: 'dataset_accounts',
  CUSTOMER_BRANDS: 'customer_brands',
  SYSTEM_PROMPT: 'system_prompt',
  RUN_SCHEDULED_AGENTS: 'run_scheduled_agents',
  AGENTS: 'agents',
  DIGEST: 'digest',
  CONNECTORS: 'connectors',
  DATA_INTEGRITY: 'data_integrity',
  DATA_CONNECTIONS: 'data_connections',
  SOURCE_INTEGRATIONS: 'source_integrations',
  EXPORT: 'export',
  EXPORT_GROUPS: 'export_groups',
  PROFILE: 'profile',
  SETTINGS: 'settings',
} as const satisfies Record<string, SettingsPageKey>

export type SettingsNavItem = {
  to: string
  label: string
  roleAllowlist: ClientAuthRole[]
  pageKey: SettingsPageKey
  /**
   * Overrides the restriction markers shown beside the label. By default they
   * are read off `roleAllowlist`, which is who the sidebar shows the tab to;
   * set this where the backend demands more than that.
   */
  access?: NavItemAccess
}

// For pages whose API needs an admin role on top of being Yogi staff, although
// the allowlist also admits Yogi viewers (who reach the page and are refused).
const YOGI_ADMIN_ACCESS: NavItemAccess = { yogiOnly: true, adminOnly: true }

export const SETTINGS_NAV_ITEMS: SettingsNavItem[] = [
  {
    to: ACCESS_URL,
    label: 'Access',
    roleAllowlist: ClientAuthPolicy.ONLY_AUTHENTICATED_USERS,
    pageKey: SETTINGS_PAGE_KEYS.ACCESS,
  },
  {
    to: HUB_SETTINGS_URL,
    label: 'Datasets',
    // Visible to any Yogi user, so a Yogi viewer can reach the page and use its
    // "Switch to admin role" button. (The FE's `YogiAdmin` also reflects the
    // PER-HUB role, so narrowing this would hide the page from a Yogi engineer
    // who is only a viewer on this hub.) The metadata and write endpoints need
    // an admin role on the hub, which a global Yogi admin has everywhere, and
    // they have no Yogi check, so a customer admin could call them directly;
    // only this nav hides them.
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.DATASETS,
  },
  {
    to: HUB_ACCOUNTS_URL,
    label: 'Accounts',
    // The backend checks only the global admin role: no hub membership and no
    // Yogi email. A Yogi viewer, or a Yogi who is only a hub admin, sees the
    // tab and is refused.
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.DATASET_ACCOUNTS,
  },
  {
    // The backend needs an admin role on the hub AND a Yogi staff email (the
    // email alone, not `is_yogi_staff_admin`). A Yogi viewer sees the tab and
    // is refused; a customer admin gets a 403.
    to: CUSTOMER_BRANDS_URL,
    label: 'Customer brands',
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.CUSTOMER_BRANDS,
  },
  {
    to: DIGEST_URL,
    // Same gate as Customer brands on the digest config and send routes: an
    // admin role on the hub plus a Yogi staff email.
    // Labeled "Executive Digest" to disambiguate from the Issue Agent
    // digest (different feature, different flag).
    label: 'Executive digest',
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.DIGEST,
  },
  {
    to: SYSTEM_PROMPT_URL,
    label: 'System prompt',
    // The backend needs only an admin role on the hub, with no Yogi check, so a
    // customer admin could call it directly; only this nav hides it.
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.SYSTEM_PROMPT,
  },
  {
    to: AGENTS_URL,
    label: 'Agents',
    // The admin routes for issue, benchmarking and insight agents need an admin
    // role on the hub plus a Yogi staff email, so a Yogi viewer sees the tab and
    // is refused.
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.AGENTS,
  },
  {
    to: RUN_SCHEDULED_AGENTS_URL,
    label: 'Run all agents',
    // The narrowest of the Yogi tabs: the route needs an admin role on the hub
    // and then `is_yogi_staff_admin` (global admin role + @meetyogi.com). A Yogi
    // who is only a hub admin passes this nav and gets a 403.
    roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
    access: YOGI_ADMIN_ACCESS,
    pageKey: SETTINGS_PAGE_KEYS.RUN_SCHEDULED_AGENTS,
  },
  {
    to: EXPORT_URL,
    label: 'Export',
    // Hides the tab from viewers in the UI. The backend only requires read
    // access to the hub's data, which members hold, so the API does not enforce
    // this restriction.
    roleAllowlist: ClientAuthPolicy.ONLY_ADMIN_USERS,
    pageKey: SETTINGS_PAGE_KEYS.EXPORT,
  },
  {
    to: INTEGRITY_REPORT_URL,
    label: 'Data Integrity',
    // Any hub member. The server grants `hub_data_integrity:read` to member,
    // admin and owner, and `scoped_hub_context_auth` is the real boundary —
    // this allowlist only decides whether the tab is offered.
    roleAllowlist: ClientAuthPolicy.ONLY_AUTHENTICATED_USERS,
    pageKey: SETTINGS_PAGE_KEYS.DATA_INTEGRITY,
  },
  {
    to: PROFILE_URL,
    label: 'Profile',
    roleAllowlist: ClientAuthPolicy.ONLY_AUTHENTICATED_USERS,
    pageKey: SETTINGS_PAGE_KEYS.PROFILE,
  },
]

// Appended to the nav list by SettingsShell ONLY when Ask Yogi v2 and
// `connectors_ask_yogi` are true. NOT part of the static
// SETTINGS_NAV_ITEMS. Role is permissive — the admin/member difference is the
// experience rendered inside the connector detail, not nav visibility.
export const CONNECTORS_NAV_ITEM: SettingsNavItem = {
  to: CONNECTORS_URL,
  label: 'Connectors',
  roleAllowlist: ClientAuthPolicy.ONLY_AUTHENTICATED_USERS,
  pageKey: SETTINGS_PAGE_KEYS.CONNECTORS,
}

// Appended to the nav list by SettingsShell ONLY for a caller holding the
// account-level `uvoc_data_sources:read` scope. NOT part of the static
// SETTINGS_NAV_ITEMS, because that array is filtered on the legacy hub-role
// axis and this page is gated on an account scope instead. The role allowlist
// below is the coarse outer filter; the scope is the real gate.
export const SOURCE_INTEGRATIONS_NAV_ITEM: SettingsNavItem = {
  to: SOURCE_INTEGRATIONS_URL,
  label: SOURCE_INTEGRATIONS_LABEL,
  roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
  pageKey: SETTINGS_PAGE_KEYS.SOURCE_INTEGRATIONS,
}

// Appended by the shell only when the account holds the `source_connection:read`
// scope. Operator-only launch posture, on the Source Integrations pattern: the
// data-connections routes authorize account-level, so access travels with the
// ACCOUNT (all hubs or none) and no hub role grants it. A different page from
// the Ask Yogi "Connectors" item — this one manages the credentials that feed
// INGESTION, not agent tools. NOT part of the static SETTINGS_NAV_ITEMS; the
// role allowlist below is the coarse outer filter, the scope is the real gate.
export const CONNECTOR_LIBRARY_NAV_ITEM: SettingsNavItem = {
  to: CONNECTOR_LIBRARY_URL,
  label: 'Data connections',
  roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
  pageKey: SETTINGS_PAGE_KEYS.DATA_CONNECTIONS,
}

// Appended by the shell only when the account holds `export_groups:read`. NOT
// part of the static SETTINGS_NAV_ITEMS, because that array is filtered on the
// legacy hub-role axis and this page is gated on an account scope instead — the
// same posture as Source Integrations and Data connections above. The role
// allowlist is the coarse outer filter; the scope is the real gate, and the
// route's `scoped_user_context_auth` is the security boundary.
export const EXPORT_GROUPS_NAV_ITEM: SettingsNavItem = {
  to: EXPORT_GROUPS_URL,
  label: 'Export groups',
  roleAllowlist: ClientAuthPolicy.ONLY_YOGI_USERS,
  pageKey: SETTINGS_PAGE_KEYS.EXPORT_GROUPS,
}

export const getAllowlistFor = (to: string): ClientAuthRole[] | undefined =>
  SETTINGS_NAV_ITEMS.find((item) => item.to === to)?.roleAllowlist

export type SettingsNavSection = {
  label: string
  /** Nav item `to` values that belong to this section. */
  urls: string[]
}

// The sidebar's grouping. Every nav item above (static or appended) must appear
// in exactly one section — the shell renders only what a section lists, so an
// unlisted item is silently missing. Order within a section is not set here:
// the shell sorts each section alphabetically by label.
export const SETTINGS_NAV_SECTIONS: SettingsNavSection[] = [
  { label: 'Preferences', urls: [PROFILE_URL] },
  {
    label: 'This hub',
    urls: [
      ACCESS_URL,
      SYSTEM_PROMPT_URL,
      CONNECTORS_URL,
      SOURCE_INTEGRATIONS_URL,
      CONNECTOR_LIBRARY_URL,
      EXPORT_URL,
      INTEGRITY_REPORT_URL,
    ],
  },
  {
    label: 'Internal Yogi controls',
    urls: [
      HUB_SETTINGS_URL,
      HUB_ACCOUNTS_URL,
      CUSTOMER_BRANDS_URL,
      DIGEST_URL,
      AGENTS_URL,
      RUN_SCHEDULED_AGENTS_URL,
      EXPORT_GROUPS_URL,
    ],
  },
]

export type NavItemAccess = {
  yogiOnly: boolean
  adminOnly: boolean
}

/**
 * Which "restricted" markers a nav item carries, read off its role allowlist.
 * An item everyone can open carries neither.
 */
export const getNavItemAccess = (
  allowlist: ClientAuthRole[],
): NavItemAccess => ({
  yogiOnly: allowlist.every((role) =>
    ClientAuthPolicy.ONLY_YOGI_USERS.includes(role),
  ),
  adminOnly: allowlist.every((role) =>
    ClientAuthPolicy.ONLY_ADMIN_USERS.includes(role),
  ),
})

export const getNavItemAccessLabel = ({
  yogiOnly,
  adminOnly,
}: NavItemAccess): string | null => {
  if (yogiOnly && adminOnly) return 'Yogi admin only'
  if (yogiOnly) return 'Yogi only'
  if (adminOnly) return 'Admin only'
  return null
}
